import {
  createEisTypeObject,
  getKey,
} from 'Quoting/utils/utilities';
import { Map as ImmutableMap } from 'immutable';
import {
  brokenToothDetailsTimePeriodCd,
  defaultMaxClosed,
  defaultMaxOpen,
  dependentChildOfDislocationRider,
  fractureDefaultValues,
  lacerationLengthMaxBenefitNumber,
  reductionDetailsDefaults,
} from './benefitDefaults';

export const createNewGVA6PlanObjectWithDefault = (
  plan: any,
  rootId: string,
  quoteParentId: string,
  coverageEffectiveDate: string,
  defaultValues: ImmutableMap<string, any>,
  situsStateCd: string
) => {
  const key = getKey(rootId, quoteParentId);
  const parentId = key.id;

  return {
    exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId, situsStateCd),
    planCd: plan.planCode,
    planGroupsLinks: [],
    _type: 'ACCBenefitPlan',
    planName: plan.name,
    _key: key,
    coverageDefs: plan.coverages
      .filter((coverage: any) => coverage.applicability === 'Mandatory')
      .map(() => createCoverageDef(defaultValues, rootId, parentId, coverageEffectiveDate, situsStateCd)),

    offerStatus: 'offered',
  };
};

const createCoverageDef = (
  defaultValues: ImmutableMap<string, any>,
  planRootId: string,
  planParentId: string,
  coverageEffectiveDate: string,
  situsStateCd: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;

  return {
    _key: key,
    code: defaultValues.get('ACCCoverageDefinition.code'),
    coverageBasisCd: defaultValues.get('ACCCoverageDefinition.coverageBasisCd'),
    benefitAmountSpouse: {
      benefitPct: defaultValues.get('ACCBenefitAmountSpouse.benefitPct'),
      ...createEisTypeObject('ACCBenefitAmountSpouse', rootId, parentId),
    },

    underwritingDetails: {
      underwritingTypeCd: defaultValues.get('ACCUnderwriting.underwritingTypeCd'),
      ...createEisTypeObject('ACCUnderwriting', rootId, parentId),
    },

    terminationAge: defaultValues.get('ACCCoverageDefinition.terminationAge'),

    benefitStructure: createACCBenefitStructure(defaultValues, rootId, parentId, situsStateCd),
    coveredEvents: defaultValues.get('ACCCoverageDefinition.coveredEvents'),
    effDate: coverageEffectiveDate,
    eligibilities: createACCEligibilities(defaultValues, rootId, parentId),

    ratingDetails: {
      rateFormatCd: defaultValues.get('ACCCoverageRating.rateFormatCd'),
      ...createEisTypeObject('ACCCoverageRating', rootId, parentId),
    },
    originalEffDate: coverageEffectiveDate,
    censusTypeCd: defaultValues.get('ACCCoverageDefinition.censusTypeCd'),
    coverageGroupsLinks: [],
    _type: 'ACCCoverageDefinition',
    fundingStructure: {
      ...createEisTypeObject('ACCFundingStructure', rootId, parentId),
      requiredGroupAccessPct: defaultValues.get('ACCFundingStructure.requiredGroupAccessPct'),
      taxabilityCd: defaultValues.get('ACCFundingStructure.taxabilityCd'),
      minParticipantsNum: defaultValues.get('ACCFundingStructure.minParticipantsNum'),
      contributionTypeCd: defaultValues.get('ACCFundingStructure.contributionTypeCd'),
      fundingTypeCd: defaultValues.get('ACCFundingStructure.fundingTypeCd'),
      payrollFrequencyCd: undefined,
    },
    benefitSchedule: createEisTypeObject('ACCBenefitSchedule', rootId, parentId),
    benefitAmountChild: {
      benefitPct: defaultValues.get('ACCBenefitAmountChild.benefitPct'),
      ...createEisTypeObject('ACCBenefitAmountChild', rootId, parentId),
    },
    offerStatus: 'offered',

    issueAge: {
      minIssueAgeChild: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeChild'),
      maxIssueAgeChild: defaultValues.get('ACCEligibilityIssueAges.maxIssueAgeChild'),
      minIssueAgeIndividual: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeIndividual'),
      minIssueAgeSpouse: defaultValues.get('ACCEligibilityIssueAges.minIssueAgeSpouse'),
      minAgeIndividual: defaultValues.get('ACCEligibilityIssueAges.minAgeIndividual'),
      minAgeChildren: defaultValues.get('ACCEligibilityIssueAges.minAgeChildren'),
      maxAgeChildren: defaultValues.get('ACCEligibilityIssueAges.maxAgeChildren'),

      ...createEisTypeObject('ACCEligibilityIssueAges', rootId, parentId),
    },
    whoCoveredCd: defaultValues.get('ACCCoverageDefinition.whoCoveredCd'),
    whoCovered: createWhoCovered(rootId, parentId),
  };
};

const createBenefitPlanExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string,
  situsStateCd?: string
) => {
  const exclusionsACC = [
    {
      exclusionEntityName: 'ACCExclAnyActOfWar',
      isExclusion: defaultValues.get('ACCExclAnyActOfWar.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclInfectionCausedByAnAccidentalCutOrWound',
      isExclusion: defaultValues.get('ACCExclInfectionCausedByAnAccidentalCutOrWound.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclHernia',
      isExclusion: defaultValues.get('ACCExclHernia.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclOnTheJobAccident',
      isExclusion: defaultValues.get('ACCExclOnTheJobAccident.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclParticipationInRiot',
      isExclusion: defaultValues.get('ACCExclParticipationInRiot.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclAeronauticsWithException',
      isExclusion: defaultValues.get('ACCExclAeronauticsWithException.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclInjuryWhileServingMilitaryNavalAirForce',
      isExclusion: defaultValues.get('ACCExclInjuryWhileServingMilitaryNavalAirForce.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclInjurySustainedWhileAlcoholDrugInfluenceUnlessPrescribed',
      isExclusion: defaultValues.get('ACCExclInjurySustainedWhileAlcoholDrugInfluenceUnlessPrescribed.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclSuicide',
      isExclusion: defaultValues.get('ACCExclSuicide.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclIllegalOccupationOrCommittingAnAssaultOrFelony',
      isExclusion: defaultValues.get('ACCExclIllegalOccupationOrCommittingAnAssaultOrFelony.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclSelfInflictedInjuryOrAction',
      isExclusion: defaultValues.get('ACCExclSelfInflictedInjuryOrAction.isExclusion'),
    },
    {
      exclusionEntityName: 'ACCExclOrganizedOrScheduledRaceOrSpeedTest',
      isExclusion: defaultValues.get('ACCExclOrganizedOrScheduledRaceOrSpeedTest.isExclusion'),
    },
  ];
  if (situsStateCd === 'MD') {
    exclusionsACC.push({
      exclusionEntityName: 'ACCExclAppropriateHealthCare',
      isExclusion: defaultValues.get('ACCExclAppropriateHealthCare.isExclusion'),
    });
  }
  if (situsStateCd === 'MI') {
    exclusionsACC.push({
      exclusionEntityName: 'ACCExclWillfulCriminalActivity',
      isExclusion: defaultValues.get('ACCExclWillfulCriminalActivity.isExclusion'),
    });
  }

  return createExclusions(exclusionsACC, rootId, parentId);
};

const createExclusions = (
  exclusions: {exclusionEntityName: string; isExclusion: boolean}[],
  rootId?: string,
  parentId?: string
) =>
  exclusions.map(({exclusionEntityName, isExclusion}) => ({
    ...createEisTypeObject(exclusionEntityName, rootId, parentId),
    isExclusion: isExclusion,
  }));

const createACCEligibilities = (defaultValues: ImmutableMap<string, any>, rootId: string, parentId: string) => {
  return [
    {
      waitingPeriodAmount: defaultValues.get('ACCEligibility.waitingPeriodAmount'),
      waitingPeriodModeCd: defaultValues.get('ACCEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('ACCEligibility.minHourlyReq'),
      waitingPeriodDefCd: defaultValues.get('ACCEligibility.waitingPeriodDefCd'),
      eligibilityTypeCd: 'ExistingEmployees',
    },
    {
      waitingPeriodModeCd: defaultValues.get('ACCEligibility.waitingPeriodModeCd'),
      minHourlyReq: defaultValues.get('ACCEligibility.minHourlyReq'),
      eligibilityTypeCd: 'NewEmployees',
    },
  ].map(e => ({...e, ...createEisTypeObject('ACCEligibility', rootId, parentId)}));
};

// Helper function for recalculating benefits based on units for dislocation rider
export const getCalculatedBenefitAmounts = (units: number) => {
  const calculatedMaxClosed = units === 1 ? defaultMaxClosed : Number(units) * defaultMaxClosed;
  const calculatedMaxOpen = units === 1 ? defaultMaxOpen : Number(units) * defaultMaxOpen;
  return {
    maximumClosedReductionBenefitAmount: getMoneyObject(calculatedMaxClosed),
    maximumOpenReductionBenefitAmount: getMoneyObject(calculatedMaxOpen),
  };
};

export const getMoneyObject = (value: number) => ({_type: 'Money', amount: value, currency: 'USD'});

const createWhoCovered = (rootId: string, parentId: string) =>
  ['Individual', 'IndividualAndSpouse', 'IndividualAndChildren', 'Family'].map(tierCd => ({
    ...createEisTypeObject('ACCTier', rootId, parentId),
    tierCd: tierCd,
  }));

export const createACCBenefitStructure = (
  defaultValues: ImmutableMap<string, any>,
  rootId: string,
  parentId: string,
  situsStateCd: string
) => {
  const dislocationUnits = defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider');
  const rehabilitationFacilityDetails = {
    incurralPeriod: defaultValues.get('ACCRehabilitationFacilityBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCRehabilitationFacilityBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCRehabilitationFacilityBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCRehabilitationFacilityBenefit.timePeriodCd'),
    maxBenefitNumber2: defaultValues.get('ACCRehabilitationFacilityBenefit.maxBenefitNumber2'),
    timePeriod2Cd: defaultValues.get('ACCRehabilitationFacilityBenefit.timePeriod2Cd'),
    ...createEisTypeObject('ACCRehabilitationFacilityBenefit', rootId, parentId),
  };

  const followUpTreatmentDetails = {
    units: defaultValues.get('ACCFollowUpTreatmentBenefit.units'),
    incurralPeriod: defaultValues.get('ACCFollowUpTreatmentBenefit.incurralPeriod'),
    isBenefitAmountCustomize: defaultValues.get('ACCFollowUpTreatmentBenefit.isBenefitAmountCustomize'),
    benefitAmount: defaultValues.get('ACCFollowUpTreatmentBenefit.standartBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCFollowUpTreatmentBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCFollowUpTreatmentBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCFollowUpTreatmentBenefit', rootId, parentId),
  };

  const xRayDetails = {
    units: defaultValues.get('ACCXRayBenefit.units'),
    incurralPeriod: defaultValues.get('ACCXRayBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCXRayBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCXRayBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCXRayBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCXRayBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCXRayBenefit', rootId, parentId),
  };

  const emergencyRoomServicesDetails = {
    unitsOfEmergencyRoomServices: defaultValues.get('ACCEmergencyRoomServicesBenefit.unitsOfEmergencyRoomServices'),
    incurralPeriod: defaultValues.get('ACCEmergencyRoomServicesBenefit.incurralPeriod'),
    customizeBenefitAmount: defaultValues.get('ACCEmergencyRoomServicesBenefit.customizeBenefitAmount'),
    benefitAmount: defaultValues.get('ACCEmergencyRoomServicesBenefit.standardBenefitAmount'),
    maxBenefitNumber: defaultValues.get('ACCEmergencyRoomServicesBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCEmergencyRoomServicesBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCEmergencyRoomServicesBenefit', rootId, parentId),
  };
  const painManagementDetails = {
    incurralPeriod: defaultValues.get('ACCPainManagementBenefit.incurralPeriod'),
    benefitAmount: defaultValues.get('ACCPainManagementBenefit.standardBenefitAmount'),
    epiduralInjection: defaultValues.get('ACCPainManagementBenefit.epiduralInjection'),
    nerveAblationProcedure: defaultValues.get('ACCPainManagementBenefit.nerveAblationProcedure'),
    maxBenefitNumber: defaultValues.get('ACCPainManagementBenefit.maxBenefitNumber'),
    timePeriodCd: defaultValues.get('ACCPainManagementBenefit.timePeriodCd'),
    ...createEisTypeObject('ACCPainManagementBenefit', rootId, parentId),
  };

  const gva6StandardStates = ['CO', 'DC', 'KY', 'MD', 'MN', 'RI', 'WA'];
  const dislocationFractureRiderDetails = {
    scheduleOfDislocationRider: gva6StandardStates.includes(situsStateCd) ? 'Standard' : 'Enhanced',
    dependentChildOfDislocationRider: dependentChildOfDislocationRider,
    timesMaximumClosedBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timesMaximumClosedBenefit'),
    timePeroidMaximumClosedBenefit: defaultValues.get(
      'ACCDislocationFractureRiderDetails.timePeroidMaximumClosedBenefit'
    ),
    timesMaximumOpenBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timesMaximumOpenBenefit'),
    timePeroidMaximumOpenBenefit: defaultValues.get('ACCDislocationFractureRiderDetails.timePeroidMaximumOpenBenefit'),
    // maximumClosedReductionBenefitAmount: getCalculatedBenefitAmounts(2000),
    // maximumOpenReductionBenefitAmount: getCalculatedBenefitAmounts(6000),
    ...getCalculatedBenefitAmounts(dislocationUnits),
    ...createEisTypeObject('ACCDislocationFractureRiderDetails', rootId, parentId),
  };

  const dislocationDetails = {
    incurralPeriod: defaultValues.get('ACCDislocationBenefit.incurralPeriod'),
    partialDislocation: defaultValues.get('ACCDislocationBenefit.partialDislocation'),
    reductionAmountDetails: [
      // Open Reduction Amount
      {
        hipAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.hipStandardBenefitAmount * dislocationUnits
        ),
        kneeNoPatellaAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount * dislocationUnits
        ),
        footBonesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.footBonesStandardBenefitAmount * dislocationUnits
        ),
        ankleAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.ankleStandardBenefitAmount * dislocationUnits
        ),
        wristAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.wristStandardBenefitAmount * dislocationUnits
        ),
        elbowAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.elbowStandardBenefitAmount * dislocationUnits
        ),
        shoulderGlenohumeralAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount * dislocationUnits
        ),
        handBonesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.handBonesStandardBenefitAmount * dislocationUnits
        ),
        collarboneAcromioclavicularAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount * dislocationUnits
        ),
        twoOrMoreFingersAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount * dislocationUnits
        ),
        twoOrMoreToesAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount * dislocationUnits
        ),
        oneFingerOrToeAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount * dislocationUnits
        ),
        kneecapAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.kneecapStandardBenefitAmount * dislocationUnits
        ),
        lowerJawAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.lowerJawStandardBenefitAmount * dislocationUnits
        ),
        otherDislocationAmount: getMoneyObject(
          3 *
            reductionDetailsDefaults.otherDislocationStandardBenefitAmount * dislocationUnits
        ),
        dislocationReductionAmountTypeCd: 'Open',
        ...createEisTypeObject('ACCDislocationReductionAmt', rootId, parentId),
      },
      // Closed Reduction Amount
      {
        hipAmount: getMoneyObject(
          reductionDetailsDefaults.hipStandardBenefitAmount * dislocationUnits
        ),
        kneeNoPatellaAmount: getMoneyObject(
          reductionDetailsDefaults.kneeNoPatellaStandardBenefitAmount * dislocationUnits
        ),
        footBonesAmount: getMoneyObject(
          reductionDetailsDefaults.footBonesStandardBenefitAmount * dislocationUnits
        ),
        ankleAmount: getMoneyObject(
          reductionDetailsDefaults.ankleStandardBenefitAmount * dislocationUnits
        ),
        wristAmount: getMoneyObject(
          reductionDetailsDefaults.wristStandardBenefitAmount * dislocationUnits
        ),
        elbowAmount: getMoneyObject(
          reductionDetailsDefaults.elbowStandardBenefitAmount * dislocationUnits
        ),
        shoulderGlenohumeralAmount: getMoneyObject(
          reductionDetailsDefaults.shoulderGlenohumeralStandardBenefitAmount * dislocationUnits
        ),
        handBonesAmount: getMoneyObject(
          reductionDetailsDefaults.handBonesStandardBenefitAmount * dislocationUnits
        ),
        collarboneAcromioclavicularAmount: getMoneyObject(
          reductionDetailsDefaults.collarboneAcromioclavicularStandardBenefitAmount * dislocationUnits
        ),
        twoOrMoreFingersAmount: getMoneyObject(
          reductionDetailsDefaults.twoOrMoreFingersStandardBenefitAmount * dislocationUnits
        ),
        twoOrMoreToesAmount: getMoneyObject(
          reductionDetailsDefaults.twoOrMoreToesStandardBenefitAmount * dislocationUnits
        ),
        oneFingerOrToeAmount: getMoneyObject(
          reductionDetailsDefaults.oneFingerOrToeStandardBenefitAmount * dislocationUnits
        ),
        kneecapAmount: getMoneyObject(
          reductionDetailsDefaults.kneecapStandardBenefitAmount * dislocationUnits
        ),
        lowerJawAmount: getMoneyObject(
          reductionDetailsDefaults.lowerJawStandardBenefitAmount * dislocationUnits
        ),
        otherDislocationAmount: getMoneyObject(
          reductionDetailsDefaults.otherDislocationStandardBenefitAmount * dislocationUnits
        ),
        dislocationReductionAmountTypeCd: 'Closed',
        ...createEisTypeObject('ACCDislocationReductionAmt', rootId, parentId),
      },
    ],
    ...createEisTypeObject('ACCDislocationBenefit', rootId, parentId),
  };

  const fractureDetails = {
    incurralPeriod: defaultValues.get('ACCFractureBenefit.incurralPeriod'),
    reductionAmountDetails: [
      // Open Reduction Amount
      {
        hipAmount: getMoneyObject(
          3 *
            fractureDefaultValues.hipThighFemurFractureAmount * dislocationUnits
        ),
        skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.skullFractureNonDepressedAmount * dislocationUnits
        ),
        pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
          3 *
            fractureDefaultValues.pelvisFractureAmount * dislocationUnits
        ),
        upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
          3 *
            fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount * dislocationUnits
        ),
        shoulderBladeScapulaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.shoulderBladeFractureAmount * dislocationUnits
        ),
        legTibiaFibulaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.legFractureAmount * dislocationUnits
        ),
        ankleAmount: getMoneyObject(
          3 *
            fractureDefaultValues.ankleFractureAmount * dislocationUnits
        ),
        kneecapPatellaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.kneecapFractureAmount * dislocationUnits
        ),
        collarboneClavicleSternumAmount: getMoneyObject(
          3 *
            fractureDefaultValues.collarBoneFractureAmount * dislocationUnits
        ),
        forearmRadiusUlnaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.forearmFractureAmount * dislocationUnits
        ),
        footOrHeelNoToesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.footFractureAmount * dislocationUnits
        ),
        handNoFingersAmount: getMoneyObject(
          3 *
            fractureDefaultValues.handOrWristFractureAmount * dislocationUnits
        ),
        lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
          3 *
            fractureDefaultValues.lowerJawFractureAmount * dislocationUnits
        ),
        twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount * dislocationUnits
        ),
        faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
          3 *
            fractureDefaultValues.bonesOfFaceFractureAmount * dislocationUnits
        ),
        noseAmount: getMoneyObject(
          3 *
            fractureDefaultValues.noseFractureAmount * dislocationUnits
        ),
        oneRibFingerOrToeAmount: getMoneyObject(
          3 *
            fractureDefaultValues.oneRibFingerToeFractureAmount * dislocationUnits
        ),
        coccyxAmount: getMoneyObject(
          3 *
            fractureDefaultValues.coccyxFractureAmount * dislocationUnits
        ),
        skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.skullFractureDepressedAmount * dislocationUnits
        ),
        vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.vertebralBodyFractureAmount * dislocationUnits
        ),
        vertebralProcessesAmount: getMoneyObject(
          3 *
            fractureDefaultValues.vertebralProcessFractureAmount * dislocationUnits
        ),
        upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
          3 *
            fractureDefaultValues.upperJawFractureAmount * dislocationUnits
        ),
        sternumAmount: getMoneyObject(
          3 *
            fractureDefaultValues.sternumFractureAmount * dislocationUnits
        ),
        anyOtherFractureAmount: getMoneyObject(
          3 *
            fractureDefaultValues.anyOtherFractureAmount * dislocationUnits
        ),
        fractureReductionAmountTypeCd: 'Open',
        ...createEisTypeObject('ACCFractureReductionAmt', rootId, parentId),
      },
      // Closed Reduction Amount
      {
        hipAmount: getMoneyObject(
          fractureDefaultValues.hipThighFemurFractureAmount * dislocationUnits
        ),
        skullFractureNonDepressedNoFaceNoseBonesAmount: getMoneyObject(
          fractureDefaultValues.skullFractureNonDepressedAmount * dislocationUnits
        ),
        pelvisInclIliumIschiumPubisAcetabulumNoCoccyxAmount: getMoneyObject(
          fractureDefaultValues.pelvisFractureAmount * dislocationUnits
        ),
        upperArmBetwElbowAndShoulderHumerusAmount: getMoneyObject(
          fractureDefaultValues.armBetweenShoulderAndElbowFractureAmount * dislocationUnits
        ),
        shoulderBladeScapulaAmount: getMoneyObject(
          fractureDefaultValues.shoulderBladeFractureAmount * dislocationUnits
        ),
        legTibiaFibulaAmount: getMoneyObject(
          fractureDefaultValues.legFractureAmount * dislocationUnits
        ),
        ankleAmount: getMoneyObject(
          fractureDefaultValues.ankleFractureAmount * dislocationUnits
        ),
        kneecapPatellaAmount: getMoneyObject(
          fractureDefaultValues.kneecapFractureAmount * dislocationUnits
        ),
        collarboneClavicleSternumAmount: getMoneyObject(
          fractureDefaultValues.collarBoneFractureAmount * dislocationUnits
        ),
        forearmRadiusUlnaAmount: getMoneyObject(
          fractureDefaultValues.forearmFractureAmount * dislocationUnits
        ),
        footOrHeelNoToesAmount: getMoneyObject(
          fractureDefaultValues.footFractureAmount * dislocationUnits
        ),
        handNoFingersAmount: getMoneyObject(
          fractureDefaultValues.handOrWristFractureAmount * dislocationUnits
        ),
        lowerJawMandibleNoAlveolarProcessAmount: getMoneyObject(
          fractureDefaultValues.lowerJawFractureAmount * dislocationUnits
        ),
        twoOrMoreRibsFingersOrToesAmount: getMoneyObject(
          fractureDefaultValues.twoOrMoreRibsFingersToesFractureAmount * dislocationUnits
        ),
        faceOrNoseBonesNoMandibleOrMaxillaAmount: getMoneyObject(
          fractureDefaultValues.bonesOfFaceFractureAmount * dislocationUnits
        ),
        noseAmount: getMoneyObject(
          fractureDefaultValues.noseFractureAmount * dislocationUnits
        ),
        oneRibFingerOrToeAmount: getMoneyObject(
          fractureDefaultValues.oneRibFingerToeFractureAmount * dislocationUnits
        ),
        coccyxAmount: getMoneyObject(
          fractureDefaultValues.coccyxFractureAmount * dislocationUnits
        ),
        skullFractureDepressedNoFaceNoseBonesAmount: getMoneyObject(
          fractureDefaultValues.skullFractureDepressedAmount * dislocationUnits
        ),
        vertebraeBodyOfNoVertebralProcessesAmount: getMoneyObject(
          fractureDefaultValues.vertebralBodyFractureAmount * dislocationUnits
        ),
        vertebralProcessesAmount: getMoneyObject(
          fractureDefaultValues.vertebralProcessFractureAmount * dislocationUnits
        ),
        upperJawMaxillaNoAlveolarProcessAmount: getMoneyObject(
          fractureDefaultValues.upperJawFractureAmount * dislocationUnits
        ),
        sternumAmount: getMoneyObject(
          fractureDefaultValues.sternumFractureAmount * dislocationUnits
        ),
        anyOtherFractureAmount: getMoneyObject(
          fractureDefaultValues.anyOtherFractureAmount * dislocationUnits
        ),
        fractureReductionAmountTypeCd: 'Closed',
        ...createEisTypeObject('ACCFractureReductionAmt', rootId, parentId),
      },
    ],
    chipFracture: dislocationFractureRiderDetails.scheduleOfDislocationRider=='Standard' ? 0.0 : 0.25,
    stressFractures: dislocationFractureRiderDetails.scheduleOfDislocationRider=='Standard' ? 0.0 : 0.10,
    ...createEisTypeObject('ACCFractureBenefit', rootId, parentId),
  };

  return {
    _key: getKey(rootId, parentId),
    offerStatus: 'offered',
    unitsOfAccidentFacilityCare: defaultValues.get('ACCBenefitStructure.unitsOfAccidentFacilityCare'),
    isHospitalAdmissionApplied: defaultValues.get('ACCBenefitStructure.isHospitalAdmissionApplied'),
    isHospitalConfinementApplied: defaultValues.get('ACCBenefitStructure.isHospitalConfinementApplied'),
    isIntensiveCareUnitConfinementApplied: defaultValues.get(
      'ACCBenefitStructure.isIntensiveCareUnitConfinementApplied'
    ),
    isObjectiveSecondOpinionApplied: defaultValues.get('ACCBenefitStructure.isObjectiveSecondOpinionApplied'),
    hospitalAdmissionDetails: {
      incurralPeriod: defaultValues.get('ACCHospitalAdmissionBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCHospitalAdmissionBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCHospitalAdmissionBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCHospitalAdmissionBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCHospitalAdmissionBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCHospitalAdmissionBenefit', rootId, parentId),
    },

    hospitalConfinementDetails: {
      incurralPeriod: defaultValues.get('ACCHospitalConfinementBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCHospitalConfinementBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCHospitalConfinementBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCHospitalConfinementBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCHospitalConfinementBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCHospitalConfinementBenefit', rootId, parentId),
    },

    intensiveCareUnitConfinementDetails: {
      incurralPeriod: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCIntensiveCareUnitConfinementBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCIntensiveCareUnitConfinementBenefit', rootId, parentId),
    },
    objectiveSecondOpinionDetails: {
      incurralPeriod: defaultValues.get('ACCObjectiveSecondOpinionBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCObjectiveSecondOpinionBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCObjectiveSecondOpinionBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCObjectiveSecondOpinionBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCObjectiveSecondOpinionBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCObjectiveSecondOpinionBenefit', rootId, parentId),
    },

    //Accident Treatment and Urgent Care Rider
    unitsOfAccAmbulanceUrgentCare: defaultValues.get('ACCBenefitStructure.unitsOfAccAmbulanceUrgentCare'),
    isAccidentPhysicianTreatmentApplied: defaultValues.get('ACCBenefitStructure.isAccidentPhysicianTreatmentApplied'),
    isAmbulanceAirApplied: defaultValues.get('ACCBenefitStructure.isAmbulanceAirApplied'),
    isAmbulanceGroundOrWaterApplied: defaultValues.get('ACCBenefitStructure.isAmbulanceGroundOrWaterApplied'),
    isUrgentCareApplied: defaultValues.get('ACCBenefitStructure.isUrgentCareApplied'),
    isXRayApplied: defaultValues.get('ACCBenefitStructure.isXRayApplied'),
    ambulanceUrgentCareBenefit: {
      incurralPeriod: defaultValues.get('ACCAmbulanceUrgentCareBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceUrgentCareBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceUrgentCareBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceUrgentCareBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceUrgentCareBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceUrgentCareBenefit', rootId, parentId),
    },
    //ACCAmbulanceAirBenefit
    ambulanceAirDetails: {
      incurralPeriod: defaultValues.get('ACCAmbulanceAirBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceAirBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceAirBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceAirBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceAirBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceAirBenefit', rootId, parentId),
    },
    //ACCAmbulanceGroundOrWaterBenefit
    ambulanceGroundOrWaterDetails: {
      incurralPeriod: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAmbulanceGroundOrWaterBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAmbulanceGroundOrWaterBenefit', rootId, parentId),
    },
    //ACCUrgentCareBenefit
    urgentCareDetails: {
      incurralPeriod: defaultValues.get('ACCUrgentCareBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCUrgentCareBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCUrgentCareBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCUrgentCareBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCUrgentCareBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCUrgentCareBenefit', rootId, parentId),
    },
    //X-Ray Benefit
    xRayDetails: xRayDetails,

    //ACCEmergencyRoomServicesBenefit
    isEmergencyRoomServicesApplied: defaultValues.get('ACCBenefitStructure.isEmergencyRoomServicesApplied'),
    emergencyRoomServicesDetails: emergencyRoomServicesDetails,

    //Benefit Enhancement  Rider
    unitsOfAccidentInjuries: Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentInjuries')),
    scheduleOfBenefitEnhancementRider: defaultValues.get('ACCBenefitStructure.scheduleOfBenefitEnhancementRider'),
    isBrainInjuryDiagnosisApplied: defaultValues.get('ACCBenefitStructure.isBrainInjuryDiagnosisApplied'),
    isBrokenToothApplied: defaultValues.get('ACCBenefitStructure.isBrokenToothApplied'),
    isBurnApplied: defaultValues.get('ACCBenefitStructure.isBurnApplied'),
    isBurnSkinGraftApplied: defaultValues.get('ACCBenefitStructure.isBurnSkinGraftApplied'),
    isComaApplied: defaultValues.get('ACCBenefitStructure.isComaApplied'),
    isLacerationApplied: defaultValues.get('ACCBenefitStructure.isLacerationApplied'),
    isParalysisApplied: defaultValues.get('ACCBenefitStructure.isParalysisApplied'),
    isRehabilitationFacilityApplied: defaultValues.get('ACCBenefitStructure.isRehabilitationFacilityApplied'),
    isFollowUpTreatmentApplied: defaultValues.get('ACCBenefitStructure.isFollowUpTreatmentApplied'),
    isMiscellaneousOutpatientSurgeryApplied: defaultValues.get(
      'ACCBenefitStructure.isMiscellaneousOutpatientSurgeryApplied'
    ),
    isOpenAbdominalOrThoracicSurgeryApplied: defaultValues.get(
      'ACCBenefitStructure.isOpenAbdominalOrThoracicSurgeryApplied'
    ),
    isTendonLigamentRotatorCuffApplied: defaultValues.get('ACCBenefitStructure.isTendonLigamentRotatorCuffApplied'),
    isGeneralAnesthesiaApplied: defaultValues.get('ACCBenefitStructure.isGeneralAnesthesiaApplied'),
    isRupturedDiscApplied: defaultValues.get('ACCBenefitStructure.isRupturedDiscApplied'),
    isBloodPlasmaPlateletsApplied: defaultValues.get('ACCBenefitStructure.isBloodPlasmaPlateletsApplied'),
    isEyeInjuryApplied: defaultValues.get('ACCBenefitStructure.isEyeInjuryApplied'),
    isMajorDiagnosticExamApplied: defaultValues.get('ACCBenefitStructure.isMajorDiagnosticExamApplied'),
    isPainManagementApplied: defaultValues.get('ACCBenefitStructure.isPainManagementApplied'),
    isCompanionNonLocalLodgingApplied: defaultValues.get('ACCBenefitStructure.isCompanionNonLocalLodgingApplied'),
    isMedicalEquipmentApplied: defaultValues.get('ACCBenefitStructure.isMedicalEquipmentApplied'),
    isMedicalSuppliesApplied: defaultValues.get('ACCBenefitStructure.isMedicalSuppliesApplied'),
    isMedicationApplied: defaultValues.get('ACCBenefitStructure.isMedicationApplied'),
    isNonLocalTransportationApplied: defaultValues.get('ACCBenefitStructure.isNonLocalTransportationApplied'),
    isPostAccidentCommonCarrierTransportationApplied: defaultValues.get(
      'ACCBenefitStructure.isPostAccidentCommonCarrierTransportationApplied'
    ),
    isProsthesisApplied: defaultValues.get('ACCBenefitStructure.isProsthesisApplied'),
    isResidenceAutomobileModificationApplied: defaultValues.get(
      'ACCBenefitStructure.isResidenceAutomobileModificationApplied'
    ),
    isTherapyApplied: defaultValues.get('ACCBenefitStructure.isTherapyApplied'),

    //Brain Injuries Section
    brainInjuryDiagnosisDetails: {
      incurralPeriod: defaultValues.get('ACCBrainInjuryDiagnosisDetails.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBrainInjuryDiagnosisDetails.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCBrainInjuryDiagnosisDetails.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBrainInjuryDiagnosisDetails.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBrainInjuryDiagnosisDetails.timePeriodCd'),
      ...createEisTypeObject('ACCBrainInjuryDiagnosisDetails', rootId, parentId),
    },

    //Broken Tooth Section
    brokenToothDetails: {
      incurralPeriod: defaultValues.get('ACCBrokenToothBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBrokenToothBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCBrokenToothBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBrokenToothBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBrokenToothBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBrokenToothBenefit', rootId, parentId),
    },

    //Burn Details
    burnDetails: {
      incurralPeriod: defaultValues.get('ACCBurnBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCBurnBenefit.customizeBenefitAmount'),
      burnDegreeDetails: [
        {
          burnSize1Amount: getMoneyObject(100),
          burnSize2Amount: getMoneyObject(500),
          burnDegreeTypeCd: "Second",
          standardBenefitAmount: getMoneyObject(100),
          ...createEisTypeObject('ACCBurnDegree', rootId, parentId),
        },
        {
          burnDegreeTypeCd: "Third",
          ...createEisTypeObject('ACCBurnDegree', rootId, parentId),
        }
      ],
      maxBenefitNumber: defaultValues.get('ACCBurnBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBurnBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBurnBenefit', rootId, parentId),
    },

    //ACCBurnSkinGraftBenefit
    burnSkinGraftDetails: {
      incurralPeriod: defaultValues.get('ACCBurnSkinGraftBenefit.incurralPeriod'),
      burnsBenefitPct: defaultValues.get('ACCBurnSkinGraftBenefit.burnsBenefitPct'),
      maxBenefitNumber: defaultValues.get('ACCBurnSkinGraftBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBurnSkinGraftBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBurnSkinGraftBenefit', rootId, parentId),
    },

    //ACCComaBenefit
    comaDetails: {
      incurralPeriod: defaultValues.get('ACCComaBenefit.incurralPeriod'),
      waitingPeriod: defaultValues.get('ACCComaBenefit.waitingPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCComaBenefit.customizeBenefitAmount'),
      benefitAmount: defaultValues.get('ACCComaBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCComaBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCComaBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCComaBenefit', rootId, parentId),
    },

    //ACCLacerationBenefit
    lacerationDetails: {
      incurralPeriod: defaultValues.get('ACCLacerationBenefit.incurralPeriod'),
      customizeBenefitAmount: defaultValues.get('ACCLacerationBenefit.customizeBenefitAmount'),
      benefitAmount:  getMoneyObject((defaultValues.get('ACCLacerationBenefit.standardBenefitAmount')?.get('amount'))),
      maxBenefitNumber: lacerationLengthMaxBenefitNumber,
      timePeriodCd: brokenToothDetailsTimePeriodCd,
      ...createEisTypeObject('ACCLacerationBenefit', rootId, parentId),
    },

    //ACCParalysisBenefit
    paralysisDetails: {
      incurralPeriod: defaultValues.get('ACCParalysisBenefit.incurralPeriod'),
      minimumDuration: defaultValues.get('ACCParalysisBenefit.minimumDuration'),
      customizeBenefitAmount: defaultValues.get('ACCParalysisBenefit.customizeBenefitAmount'),
      oneOrTwoLimbsAmount: defaultValues.get('ACCParalysisBenefit.standardBenefitAmount'),
      threeOrFourLimbsAmount: defaultValues.get('ACCParalysisBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCParalysisBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCParalysisBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCParalysisBenefit', rootId, parentId),
    },

    //ACCRehabilitation
    rehabilitationFacilityDetails: rehabilitationFacilityDetails,

    //ACCFollowUp
    followUpTreatmentDetails: followUpTreatmentDetails,

    //ACCMiscellaneousOutpatientSurgeryBenefit
    miscellaneousOutpatientSurgeryDetails: {
      incurralPeriod: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMiscellaneousOutpatientSurgeryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMiscellaneousOutpatientSurgeryBenefit', rootId, parentId),
    },

    //ACCOpenAbdominalOrThoracicSurgeryBenefit
    openAbdominalOrThoracicSurgeryDetails: {
      incurralPeriod: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.incurralPeriod'),
      benefitAmount: getMoneyObject(1000),
      maxBenefitNumber: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCOpenAbdominalOrThoracicSurgeryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCOpenAbdominalOrThoracicSurgeryBenefit', rootId, parentId),
    },

    //ACCTendonLigamentRotatorCuffBenefit
    tendonLigamentRotatorCuffDetails: {
      incurralPeriod: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.incurralPeriod'),
      maxBenefitNumber: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.timePeriodCd'),
      withoutRepairAmount: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.withoutRepairStandardBenefitAmount'),
      withRepairAmount: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.withRepairStandardBenefitAmount'),
      //withoutRepairAmount: defaultValues.get('ACCTendonLigamentRotatorCuffBenefit.withoutRepairStandardBenefitAmount'),
      ...createEisTypeObject('ACCTendonLigamentRotatorCuffBenefit', rootId, parentId),
    },

    //ACCGeneralAnesthesiaBenefit
    generalAnesthesiaDetails: {
      incurralPeriod: defaultValues.get('ACCGeneralAnesthesiaBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCGeneralAnesthesiaBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCGeneralAnesthesiaBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCGeneralAnesthesiaBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCGeneralAnesthesiaBenefit', rootId, parentId),
    },
    //ACCRupturedDiscBenefit
    rupturedDiscDetails: {
      incurralPeriod: defaultValues.get('ACCRupturedDiscBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCRupturedDiscBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCRupturedDiscBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCRupturedDiscBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCRupturedDiscBenefit', rootId, parentId),
    },
    //ACCBloodPlasmaPlateletsBenefit
    bloodPlasmaPlateletsDetails: {
      incurralPeriod: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCBloodPlasmaPlateletsBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCBloodPlasmaPlateletsBenefit', rootId, parentId),
    },

    //ACCEyeInjuryBenefit
    eyeInjuryDetails: {
      incurralPeriod: defaultValues.get('ACCEyeInjuryBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCEyeInjuryBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCEyeInjuryBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCEyeInjuryBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCEyeInjuryBenefit', rootId, parentId),
    },

    //COMPUTED TOMOGRAPHY (CT) SCAN AND MRI (First treatment within 30 days)
    majorDiagnosticExamDetails: {
      incurralPeriod: defaultValues.get('ACCMajorDiagnosticExamBenefit.incurralPeriod'),
      cATCustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.cATCustomizeBenefitAmount'),
      cATBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount'),
      mRICustomizeBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.mRICustomizeBenefitAmount'),
      mRIBenefitAmount: defaultValues.get('ACCMajorDiagnosticExamBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMajorDiagnosticExamBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMajorDiagnosticExamBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMajorDiagnosticExamBenefit', rootId, parentId),
    },

    //ACCPainManagementBenefit
    painManagementDetails: painManagementDetails,
    companionNonLocalLodgingDetails: {
      incurralPeriod: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.incurralPeriod'),
      distanceToHospitalMoreThanMilesCd: defaultValues.get(
        'ACCCompanionNonLocalLodgingBenefit.distanceToHospitalMoreThanMilesCd'
      ),
      benefitAmount: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCCompanionNonLocalLodgingBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCCompanionNonLocalLodgingBenefit', rootId, parentId),
    },

    medicalEquipmentDetails: {
      incurralPeriod: defaultValues.get('ACCMedicalEquipmentBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicalEquipmentBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicalEquipmentBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicalEquipmentBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicalEquipmentBenefit', rootId, parentId),
    },

    medicalSuppliesDetails: {
      incurralPeriod: defaultValues.get('ACCMedicalSuppliesBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicalSuppliesBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicalSuppliesBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicalSuppliesBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicalSuppliesBenefit', rootId, parentId),
    },

    medicationDetails: {
      incurralPeriod: defaultValues.get('ACCMedicationBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCMedicationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCMedicationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCMedicationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCMedicationBenefit', rootId, parentId),
    },

    nonLocalTransportationDetails: {
      incurralPeriod: defaultValues.get('ACCNonLocalTransportationBenefit.incurralPeriod'),
      travelDistanceMoreThan: defaultValues.get('ACCNonLocalTransportationBenefit.travelDistanceMoreThan'),
      benefitAmount: defaultValues.get('ACCNonLocalTransportationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCNonLocalTransportationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCNonLocalTransportationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCNonLocalTransportationBenefit', rootId, parentId),
    },

    postAccidentCommonCarrierTransportationDetails: {
      incurralPeriod: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.incurralPeriod'),
      travelDistanceMoreThan: defaultValues.get(
        'ACCPostAccidentCommonCarrierTransportationBenefit.travelDistanceMoreThan'
      ),
      benefitAmount: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCPostAccidentCommonCarrierTransportationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCPostAccidentCommonCarrierTransportationBenefit', rootId, parentId),
    },

    // Prosthesis Details
    prosthesisDetails: {
      incurralPeriod: defaultValues.get('ACCProsthesisBenefit.incurralPeriod'),
      oneDeviceAmount: defaultValues.get('ACCProsthesisBenefit.standardBenefitAmount'),
      twoOrMoreDevicesAmount: defaultValues.get('ACCProsthesisBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCProsthesisBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCProsthesisBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCProsthesisBenefit', rootId, parentId),
    },

    // Residence Automobile Modification Details
    residenceAutomobileModificationDetails: {
      incurralPeriod: defaultValues.get('ACCResidenceAutomobileModificationBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCResidenceAutomobileModificationBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCResidenceAutomobileModificationBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCResidenceAutomobileModificationBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCResidenceAutomobileModificationBenefit', rootId, parentId),
    },

    // Therapy Details
    therapyDetails: {
      incurralPeriod: defaultValues.get('ACCTherapyBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCTherapyBenefit.standardBenefitAmount'),
      isAlternateTherapyApplied: defaultValues.get('ACCTherapyBenefit.isAlternateTherapyApplied'),
      maxBenefitNumber: defaultValues.get('ACCTherapyBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCTherapyBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCTherapyBenefit', rootId, parentId),
    },
    
    //ACCAccidentalDeathAndDismemberment
    unitsOfAccidentalDeathAndDismemberment:
      Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')),
    isAccidentalDeathApplied: defaultValues.get('ACCBenefitStructure.isAccidentalDeathApplied'),
    accidentalDeathDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDeathBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCAccidentalDeathBenefit.standardBenefitAmount'),
      ...createEisTypeObject('ACCAccidentalDeathBenefit', rootId, parentId),
    },
    isAccidentalDeathCommonCarrierApplied: defaultValues.get(
      'ACCBenefitStructure.isAccidentalDeathCommonCarrierApplied'
    ),
    accidentalDeathCommonCarrierDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDeathCommonCarrierBenefit.incurralPeriod'),
      benefitAmount: defaultValues.get('ACCAccidentalDeathCommonCarrierBenefit.standardBenefitAmount'),
      ...createEisTypeObject('ACCAccidentalDeathCommonCarrierBenefit', rootId, parentId),
    },

    isAccidentalDismembermentApplied: defaultValues.get('ACCBenefitStructure.isAccidentalDismembermentApplied'),
    accidentalDismembermentDetails: {
      incurralPeriod: defaultValues.get('ACCAccidentalDismembermentBenefit.incurralPeriod'),
      bothArmsOrHandsLossAmount: defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount'),
      bothLegsOrFeetLossAmount: defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount'),
      oneHandOrArmAndOneLegOrFootLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      armOrHandLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      legOrFootLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          0.5
      ),
      oneOrMoreFingersLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          (situsStateCd === 'NH' ? 0.05 : 0.1)
      ),
      oneOrMoreToesLossAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1) *
          (situsStateCd === 'NH' ? 0.05 : 0.1)
      ),
      maximumBenefitAmount: getMoneyObject(
        defaultValues.get('ACCAccidentalDismembermentBenefit.standardBenefitAmount')?.get('amount') *
          (Number(defaultValues.get('ACCBenefitStructure.unitsOfAccidentalDeathAndDismemberment')) || 1)
      ),
      maxBenefitNumber: defaultValues.get('ACCAccidentalDismembermentBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCAccidentalDismembermentBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCAccidentalDismembermentBenefit', rootId, parentId),
    },
    isFunctionalLossApplied: defaultValues.get('ACCBenefitStructure.isFunctionalLossApplied'),

    functionalLossDetails: {
      incurralPeriod: defaultValues.get('ACCFunctionalLossBenefit.incurralPeriod'),
      minimumDuration: defaultValues.get('ACCFunctionalLossBenefit.minimumDuration'),
      hearingLossAmount: defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount'),
      sightLossAmount: defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount'),
      speechLossAmount: defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount'),
      maximumBenefitAmount: defaultValues.get('ACCFunctionalLossBenefit.standardBenefitAmount'),
      maxBenefitNumber: defaultValues.get('ACCFunctionalLossBenefit.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCFunctionalLossBenefit.timePeriodCd'),
      ...createEisTypeObject('ACCFunctionalLossBenefit', rootId, parentId),
    },

    //ACCDislocationandFractureRiderBenifit
    isDislocationAndFractureRiderBenefitApplied: defaultValues.get(
      'ACCBenefitStructure.isDislocationAndFractureRiderBenefitApplied'
    ),
    unitsOfDislocationRider: Number(defaultValues.get('ACCBenefitStructure.unitsOfDislocationRider')), 
    dislocationFractureRiderDetails: dislocationFractureRiderDetails,
    isDislocationApplied: defaultValues.get('ACCDislocationBenefit.isDislocationApplied'),
    dislocationDetails: dislocationDetails,
    isFractureApplied: defaultValues.get('ACCFractureBenefit.isFractureApplied'),
    fractureDetails: fractureDetails,


    //AdditionalOptionsandBenefits
    _type: 'ACCBenefitStructure',
    isPortabilityApplied: defaultValues.get('ACCBenefitStructure.isPortabilityApplied') ?? true,
    isOutpatientPhysicianTreatmentsApplied: !['MN', 'MI', 'WA'].includes(situsStateCd),
    portabilityDetails: {
      ...createEisTypeObject('ACCPortability', rootId, parentId),
      limitPortedCovDurationInd: defaultValues.get('ACCPortability.limitPortedCovDurationInd'),
      covDuration: defaultValues.get('ACCPortability.covDuration'),
    },

    outpatientPhysicianTreatmentsDetails: {
      typeOfRider: 'HSA', 
      units: defaultValues.get('ACCOutpatientPhysicianTreatments.units'),
      maxBenefitNumber: defaultValues.get('ACCOutpatientPhysicianTreatments.maxBenefitNumber'),
      timePeriodCd: defaultValues.get('ACCOutpatientPhysicianTreatments.timePeriodCd'),
      benefitAmount: defaultValues.get('ACCOutpatientPhysicianTreatments.benefitAmount'),
      exclusions: createOutpatientExclusions(defaultValues, rootId, parentId),
      ...createEisTypeObject('ACCOutpatientPhysicianTreatments', rootId, parentId),
    },
  };
  
};

const createOutpatientExclusions = (
  defaultValues: ImmutableMap<string, any>,
  rootId?: string,
  parentId?: string,
) => {
  
  const exclusionsMapping = {
    'ACCExclAnyActOfWar': 'AccOptExclAnyActOfWar',
    'ACCExclSuicide': 'AccOptExclSuicideOrAnyAttempt',
    'ACCExclSelfInflictedInjuryOrAction': 'AccOptExclIntentionallySelfInflictedInjuryOrAction',
    'ACCExclAeronauticsWithException': 'AccOptExclAeronauticsWithException',
    'ACCExclParticipationInRiot': 'AccOptExclParticipationInRiot',
    'ACCExclIllegalOccupationOrCommittingAnAssaultOrFelony': 'AccOptExclIllegalOccupationOrCommittingAnAssaultOrFelony',
    'ACCExclOrganizedOrScheduledRaceOrSpeedTest': 'AccOptExclOrganizedOrScheduledRaceOrSpeedTest',
    'ACCExclInjurySustainedWhileAlcoholDrugInfluenceUnlessPrescribed': 'AccOptExclInjurySustainedAlcoholDrug',
    'ACCExclOnTheJobAccident': 'AccOptExclOnJobAccident',
    'ACCExclInjuryWhileServingMilitaryNavalAirForce': 'AccOptExclInjuryMilitaryNavelAirbase',
    'ACCExclAppropriateHealthCare': 'AccOptExclHealthCareServices'
  };

  return Object.keys(exclusionsMapping).map((optionEntityName: string) => {
    const updatedOptionEntityName = exclusionsMapping[optionEntityName];

    return {
      ...createEisTypeObject(updatedOptionEntityName, rootId, parentId),
      isExclusion: defaultValues.get(`${optionEntityName}.isExclusion`),
    };
  });
};
